/* Sub-Supplier Admin Workbench — Infosys Admin only.

   Route: /infoshop/admin/sub-suppliers

   Captures the full sub-supplier contract record:
     - Display     : name, code, tagline, description, contract_name
     - Logo        : file upload (PNG/JPG/SVG/WEBP ≤ 2 MB)
     - Commercial  : advantage, open_buy, currency, default_lead_days,
                     default_category, default_unspsc
     - Protocol    : cXML / OCI
     - From / To   : domain, identity, sender_domain, sender_identity
     - Secret      : shared_secret (visible to admins only)
     - Endpoint    : punchout_url
     - Mode flags  : enabled, mock, requires_ad_login (Microsoft AD prompt)
*/
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Plus, Trash2, Pencil, Save, X, ArrowLeft, Store, Loader2, KeyRound,
  CheckCircle2, XCircle, Upload, ShieldCheck, FileSpreadsheet, Download,
  Globe, AlertCircle, Sparkles, PlugZap, HelpCircle,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/infoshop/admin/sub-suppliers`;

const EMPTY_FORM = {
  name: "",
  code: "",
  description: "",
  contract_name: "",
  tagline: "",
  logo_url: "",
  logo_path: "",
  protocol: "cXML",
  domain: "DUNS",
  identity: "",
  sender_domain: "DUNS",
  sender_identity: "",
  shared_secret: "",
  punchout_url: "",
  // OCI fields (used only when protocol==="OCI")
  oci_username: "",
  oci_password: "",
  oci_language: "en-US",
  oci_okcode: "ADOI",
  oci_target: "_top",
  oci_caller: "CTLG",
  oci_hook_url_override: "",
  oci_po_email_test: "",
  oci_po_email_prod: "",
  advantage: false,
  open_buy: true,
  currency: "USD",
  default_category: "Sub-Supplier Catalog",
  default_unspsc: "",
  default_lead_days: 7,
  enabled: false,
  mock: true,
  requires_ad_login: false,
  countries: [],
};

// Country / region picker — `ALL` and `EU` are group tokens that the
// backend expands at filter time. Specific ISO codes can be combined
// with each other (but not with ALL).
const COUNTRY_OPTIONS = [
  { code: "ALL", label: "All countries (visible everywhere)" },
  { code: "EU",  label: "All Europe (EU 27)" },
  { code: "NL",  label: "Netherlands" },
  { code: "DE",  label: "Germany" },
  { code: "FR",  label: "France" },
  { code: "GB",  label: "United Kingdom" },
  { code: "ES",  label: "Spain" },
  { code: "IT",  label: "Italy" },
  { code: "BE",  label: "Belgium" },
  { code: "PL",  label: "Poland" },
  { code: "SE",  label: "Sweden" },
  { code: "US",  label: "United States" },
  { code: "CA",  label: "Canada" },
  { code: "MX",  label: "Mexico" },
  { code: "BR",  label: "Brazil" },
  { code: "AR",  label: "Argentina" },
  { code: "CN",  label: "China" },
  { code: "JP",  label: "Japan" },
  { code: "IN",  label: "India" },
  { code: "AU",  label: "Australia" },
  { code: "ZA",  label: "South Africa" },
];

function resolveLogo(s) {
  return s?.logo_path ? `${BACKEND_URL}${s.logo_path}` : s?.logo_url || null;
}

export default function SubSuppliersAdminPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef(null);
  const bulkInputRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [aiMapEnabled, setAiMapEnabled] = useState(true);
  const [aiPreview, setAiPreview] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);

  const isAdmin = user?.role === "admin";

  const [connTesting, setConnTesting] = useState(false);
  const [connTest, setConnTest] = useState(null);
  useEffect(() => { setConnTest(null); }, [editing]);

  const testConnection = async () => {
    if (!editing || editing === "new") {
      toast.message("Save the sub-supplier first, then run the connection test.");
      return;
    }
    setConnTesting(true);
    setConnTest(null);
    try {
      const { data } = await axios.post(`${API}/${editing}/test-connection`);
      setConnTest(data);
      if (data.ok === true) toast.success(`Catalog reachable · HTTP ${data.http_status} in ${data.elapsed_ms}ms`);
      else if (data.ok === false) toast.error(data.reason || "Connection test failed");
      else toast.message(data.reason || "Ambiguous response — review details below");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Connection test failed";
      setConnTest({ ok: false, verdict: "error", reason: msg });
      toast.error(msg);
    } finally {
      setConnTesting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API);
      setList(data.sub_suppliers || []);
    } catch {
      toast.error("Failed to load sub-suppliers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...EMPTY_FORM });
    setEditing("new");
  };
  const openEdit = (s) => {
    setForm({ ...EMPTY_FORM, ...s });
    setEditing(s.id);
  };
  const close = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    if (!form.name || !form.code || !form.punchout_url || !form.identity || !form.sender_identity || !form.shared_secret) {
      toast.error("Name, code, PunchOut URL, Identity, Sender Identity and Shared Secret are required");
      return;
    }
    // Mirror the server-side `_validate_for_enable`: enabling a row
    // requires BOTH a country tag AND a company logo. For NEW rows the
    // admin may have *picked* a logo file even though `logo_path` is
    // still empty (it gets uploaded as part of the save sequence below)
    // — treat that as "logo present" so we don't block them with a
    // chicken-and-egg validation error.
    const pickedFile = fileInputRef.current?.files?.[0];
    const hasLogo = (form.logo_path || "").trim() || (form.logo_url || "").trim() || pickedFile;
    if (form.enabled) {
      if ((form.countries || []).length === 0) {
        toast.error("Pick at least one country / region before enabling this sub-supplier.");
        return;
      }
      if (!hasLogo) {
        toast.error("Upload a company logo before enabling this sub-supplier.");
        return;
      }
    }
    setSaving(true);
    try {
      let savedId = editing;
      const wantsEnabled = !!form.enabled;
      const isNew = editing === "new";
      if (isNew) {
        // Backend `_validate_for_enable` would reject a brand-new row
        // with `enabled=true` because logo_path isn't set yet — solve
        // the chicken-and-egg by always creating disabled, uploading
        // the logo, then flipping enabled ON if the admin requested it.
        const createPayload = pickedFile ? { ...form, enabled: false } : form;
        const { data } = await axios.post(API, createPayload);
        savedId = data.id;
        toast.success(`${form.name} created`);
      } else {
        // Edit flow: if user ticked Enabled but the existing row had no
        // logo AND they picked a fresh file, do the same dance —
        // temporarily save as disabled, upload, then flip ON.
        const needsLogoFirst = wantsEnabled && pickedFile && !(form.logo_path || form.logo_url);
        const updatePayload = needsLogoFirst ? { ...form, enabled: false } : form;
        await axios.put(`${API}/${editing}`, updatePayload);
        toast.success(`${form.name} updated`);
      }
      if (pickedFile && savedId) {
        await uploadLogo(savedId, pickedFile);
      }
      // Final flip ON if admin wanted it and we created/updated as disabled
      // earlier to satisfy the logo-gate ordering.
      if (savedId && wantsEnabled) {
        // Check current state with a fresh GET to avoid sending stale data
        try {
          const { data: refreshed } = await axios.get(`${API}/${savedId}`);
          if (!refreshed.enabled) {
            await axios.post(`${API}/${savedId}/toggle`);
          }
        } catch {
          /* non-fatal — admin can toggle manually if this final step fails */
        }
      }
      close();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (supplierId, file) => {
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/${supplierId}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Merge the refreshed sub_supplier row so logo_path lands in form
      // state synchronously — admin can then immediately tick `enabled`
      // without the validator screaming about a missing logo.
      const refreshed = data?.sub_supplier || {};
      setForm((f) => ({
        ...f,
        ...refreshed,
        logo_path: data.logo_path || refreshed.logo_path || f.logo_path,
        // Bust the browser cache so the new logo image is fetched.
        logo_cache_buster: Date.now(),
      }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/${s.id}`);
      toast.success(`${s.name} deleted`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggle = async (s) => {
    try {
      await axios.post(`${API}/${s.id}/toggle`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toggle failed");
    }
  };

  const handleBulkFile = async (file) => {
    if (!file) return;
    // When AI mapping is enabled, ALWAYS preview first — the admin
    // confirms the mapping before any sub-suppliers are inserted. The
    // strict-template path still works the same.
    if (aiMapEnabled) {
      setPendingFile(file);
      const fd = new FormData();
      fd.append("file", file);
      setBulkUploading(true);
      setBulkResult(null);
      setAiPreview(null);
      try {
        const { data } = await axios.post(`${API}/bulk-preview`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAiPreview(data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "AI preview failed");
        setPendingFile(null);
      } finally {
        setBulkUploading(false);
        if (bulkInputRef.current) bulkInputRef.current.value = "";
      }
      return;
    }
    // Strict-template path — no AI, direct upload
    await doBulkUpload(file, false);
  };

  const doBulkUpload = async (file, useAi) => {
    const fd = new FormData();
    fd.append("file", file);
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const { data } = await axios.post(`${API}/bulk-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params: useAi ? { ai_map: true } : {},
      });
      setBulkResult(data);
      setAiPreview(null);
      setPendingFile(null);
      toast.success(`${data.created} created · ${data.updated} updated${data.errors?.length ? ` · ${data.errors.length} error(s)` : ""}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setBulkUploading(false);
      if (bulkInputRef.current) bulkInputRef.current.value = "";
    }
  };

  const confirmAiUpload = () => {
    if (pendingFile) doBulkUpload(pendingFile, true);
  };
  const cancelAiPreview = () => {
    setAiPreview(null);
    setPendingFile(null);
  };

  const downloadTemplate = () => {
    window.location.href = `${API}/bulk-template.csv`;
  };

  const toggleCountry = (code) => {
    setForm((f) => {
      const current = new Set(f.countries || []);
      // ALL and EU/ISO codes are mutually exclusive — picking ALL clears
      // the rest; picking a specific code clears ALL.
      if (code === "ALL") {
        return { ...f, countries: current.has("ALL") ? [] : ["ALL"] };
      }
      current.delete("ALL");
      if (current.has(code)) {
        current.delete(code);
      } else {
        current.add(code);
      }
      return { ...f, countries: Array.from(current) };
    });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="text-center">
          <h1 className="font-display text-2xl font-bold text-slate-900">Admin only</h1>
          <p className="text-sm text-slate-600 mt-2">
            Sub-supplier configuration is restricted to Infosys BPM administrators.
          </p>
          <Link to="/infoshop" className="text-[#0072CE] font-semibold text-sm hover:underline mt-3 inline-block">
            Back to UniBuy™
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link
            to="/infoshop"
            data-testid="ss-admin-back-link"
            className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft size={14} /> Back to UniBuy™
          </Link>
          <div className="flex items-center gap-2 ml-2">
            <Store size={18} className="text-[#0072CE]" />
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Admin Workbench
              </div>
              <h1 className="font-display font-bold text-slate-900">Sub-Supplier Catalog</h1>
            </div>
          </div>
          <Button
            onClick={openNew}
            data-testid="ss-admin-new-btn"
            className="ml-auto bg-[#0072CE] hover:bg-[#005BA6] text-white"
          >
            <Plus size={14} className="mr-1.5" /> New Sub-Supplier
          </Button>
          <Button
            onClick={downloadTemplate}
            variant="outline"
            data-testid="ss-admin-template-btn"
            className="border-slate-300"
          >
            <Download size={14} className="mr-1.5" /> Template
          </Button>
          <Button
            onClick={() => bulkInputRef.current?.click()}
            variant="outline"
            disabled={bulkUploading}
            data-testid="ss-admin-bulk-btn"
            className="border-[#0072CE] text-[#0072CE] hover:bg-[#0072CE]/10"
          >
            {bulkUploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1.5" />}
            Bulk upload (XLSX/CSV)
          </Button>
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 select-none cursor-pointer" title="Use Gemini to auto-map column names to schema fields">
            <input
              type="checkbox"
              checked={aiMapEnabled}
              onChange={(e) => setAiMapEnabled(e.target.checked)}
              data-testid="ss-admin-bulk-ai-toggle"
              className="rounded border-slate-300"
            />
            <Sparkles size={12} className="text-[#0072CE]" />
            <span className="font-semibold">AI auto-map</span>
          </label>
          <input
            ref={bulkInputRef}
            type="file"
            accept=".xlsx,.csv"
            className="hidden"
            data-testid="ss-admin-bulk-input"
            onChange={(e) => handleBulkFile(e.target.files?.[0])}
          />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {aiPreview && (
          <div className="mb-4 bg-violet-50 border-2 border-violet-300 rounded-xl p-4" data-testid="ss-ai-preview">
            <div className="flex items-start gap-3">
              <Sparkles size={18} className="text-violet-700 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-bold text-violet-900 flex items-center gap-2">
                  AI-mapped {aiPreview.row_count} row{aiPreview.row_count === 1 ? "" : "s"} — review before importing
                </div>
                <div className="text-xs text-violet-800 mt-0.5">
                  Gemini 3 Flash mapped your column headers to UniBuy™ schema fields. Confirm to import (all rows will be created DISABLED — you&apos;ll still need to add country tags and a logo before enabling).
                </div>
                {aiPreview.ai_mapping?.notes && (
                  <div className="mt-1.5 text-xs italic text-violet-700">Note from AI: {aiPreview.ai_mapping.notes}</div>
                )}
                <div className="mt-3 bg-white border border-violet-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Column mapping</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs font-mono">
                    {Object.entries(aiPreview.ai_mapping?.mapping || {}).map(([src, tgt]) => {
                      const conf = (aiPreview.ai_mapping?.confidence || {})[tgt] ?? null;
                      return (
                        <div key={src} className="flex items-center gap-1.5 px-1" data-testid={`ss-ai-map-${tgt}`}>
                          <span className="text-slate-500 truncate" title={src}>{src}</span>
                          <span className="text-slate-300">→</span>
                          <span className="text-[#0072CE] font-bold">{tgt}</span>
                          {conf !== null && (
                            <span className={`ml-auto text-[10px] px-1 rounded ${conf >= 0.9 ? "bg-emerald-100 text-emerald-800" : conf >= 0.7 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>
                              {Math.round(conf * 100)}%
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(aiPreview.ai_mapping?.unmapped || []).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-100 text-xs">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-2">Unmapped:</span>
                      {aiPreview.ai_mapping.unmapped.map((u) => (
                        <span key={u} className="inline-block text-[10px] font-mono bg-slate-100 text-slate-600 px-1 py-0.5 rounded mr-1">{u}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="mt-3 bg-white border border-violet-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">First {aiPreview.preview?.length} row preview</div>
                  <div className="space-y-1.5 text-xs">
                    {(aiPreview.preview || []).map((p) => (
                      <div key={p.row} className={`px-2 py-1.5 rounded border ${p.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
                        <span className="font-mono text-slate-500">Row {p.row}:</span>{" "}
                        {p.ok ? (
                          <span>
                            <strong>{p.doc.code}</strong> · {p.doc.name}
                            <span className="text-slate-500"> · {p.doc.punchout_url}</span>
                          </span>
                        ) : (
                          <span className="text-rose-700">{p.error}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    onClick={confirmAiUpload}
                    disabled={bulkUploading}
                    data-testid="ss-ai-confirm-btn"
                    className="bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    {bulkUploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <CheckCircle2 size={14} className="mr-1.5" />}
                    Confirm import · {aiPreview.row_count} row{aiPreview.row_count === 1 ? "" : "s"}
                  </Button>
                  <Button
                    onClick={cancelAiPreview}
                    variant="outline"
                    data-testid="ss-ai-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
              <button onClick={cancelAiPreview} className="text-violet-700 hover:text-violet-900" data-testid="ss-ai-close">
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        {bulkResult && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="ss-bulk-result">
            <div className="flex items-start gap-3">
              <FileSpreadsheet size={18} className="text-blue-700 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-bold text-blue-900">
                  Bulk upload complete · {bulkResult.created} created · {bulkResult.updated} updated
                  {bulkResult.errors?.length ? ` · ${bulkResult.errors.length} error(s)` : ""}
                </div>
                <div className="text-xs text-blue-800 mt-1">{bulkResult.note}</div>
                {bulkResult.errors?.length > 0 && (
                  <ul className="mt-2 text-xs text-rose-900 space-y-0.5">
                    {bulkResult.errors.slice(0, 6).map((e, i) => (
                      <li key={i}>
                        <AlertCircle size={11} className="inline mr-1 text-rose-700" />
                        Row {e.row}{e.code ? ` (${e.code})` : ""}: {e.error}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button onClick={() => setBulkResult(null)} className="text-blue-700 hover:text-blue-900" data-testid="ss-bulk-result-close">
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex items-baseline justify-between">
            <div>
              <h2 className="font-display font-semibold text-slate-900">Configured sub-suppliers</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {list.length} configured · {list.filter((s) => s.enabled).length} active ·{" "}
                {list.filter((s) => !s.mock).length} production · {list.filter((s) => s.requires_ad_login).length} with MS AD interstitial.
              </p>
            </div>
          </div>
          {loading && (
            <div className="p-12 text-center">
              <Loader2 size={20} className="animate-spin mx-auto text-slate-400" />
            </div>
          )}
          {!loading && list.length === 0 && (
            <div className="p-12 text-center text-sm text-slate-500">
              No sub-suppliers configured. Click <strong>New Sub-Supplier</strong> to add one.
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {list.map((s) => {
              const logo = resolveLogo(s);
              return (
                <div
                  key={s.id}
                  data-testid={`ss-row-${s.code}`}
                  className="px-5 py-4 grid grid-cols-12 gap-3 items-center hover:bg-slate-50"
                >
                  <div className="col-span-5 flex items-center gap-3">
                    {logo ? (
                      <img
                        src={logo}
                        alt={s.name}
                        className="h-10 w-10 rounded-md bg-white border border-slate-200 object-contain p-0.5"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-md bg-[#0072CE]/10 border border-[#0072CE]/20 text-[#0072CE] font-bold flex items-center justify-center">
                        {s.code.slice(0, 2)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-slate-900 text-sm">{s.name}</div>
                      <div className="text-[11px] text-slate-500 line-clamp-1">{s.description || s.tagline}</div>
                      <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                        {s.code} · {s.protocol || "cXML"} · {s.domain}/{s.identity}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-4 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">PunchOut endpoint</div>
                    <div className="text-xs text-slate-700 font-mono truncate" title={s.punchout_url}>
                      {s.punchout_url}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1 inline-flex items-center gap-1.5">
                      <KeyRound size={9} /> Secret: <span className="font-mono">{"•".repeat(Math.min(8, s.shared_secret?.length || 0))}</span>
                    </div>
                  </div>
                  <div className="col-span-1 text-xs">
                    <div className="font-medium text-slate-700">{s.currency}</div>
                    <div className="text-slate-500">Lead {s.default_lead_days}d</div>
                    {(s.countries || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-0.5" title="Visible to country / region">
                        {(s.countries || []).slice(0, 3).map((c) => (
                          <span
                            key={c}
                            className={`text-[9px] font-mono px-1 py-px rounded ${
                              c === "ALL" || c === "EU"
                                ? "bg-violet-100 text-violet-700 border border-violet-200"
                                : "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            }`}
                          >
                            {c}
                          </span>
                        ))}
                        {(s.countries || []).length > 3 && (
                          <span className="text-[9px] text-slate-500">+{(s.countries || []).length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="col-span-2 flex flex-wrap items-center justify-end gap-1.5">
                    {s.advantage && (
                      <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                        Advantage
                      </span>
                    )}
                    {s.mock ? (
                      <span className="text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Mock
                      </span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">
                        Production
                      </span>
                    )}
                    {s.requires_ad_login && (
                      <span className="text-[9px] uppercase tracking-wider font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                        <ShieldCheck size={9} /> MS AD
                      </span>
                    )}
                    <button
                      onClick={() => toggle(s)}
                      data-testid={`ss-toggle-${s.code}`}
                      title={s.enabled ? "Disable" : "Enable"}
                      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                        s.enabled
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                          : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200"
                      }`}
                    >
                      {s.enabled ? <CheckCircle2 size={10} /> : <XCircle size={10} />}
                      {s.enabled ? "On" : "Off"}
                    </button>
                    <button
                      onClick={() => openEdit(s)}
                      data-testid={`ss-edit-${s.code}`}
                      className="text-slate-500 hover:text-[#0072CE] p-1.5 rounded hover:bg-slate-100"
                      title="Edit"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => remove(s)}
                      data-testid={`ss-delete-${s.code}`}
                      className="text-slate-400 hover:text-rose-600 p-1.5 rounded hover:bg-rose-50"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>

      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ss-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing === "new" ? "New Sub-Supplier" : `Edit ${form.name || ""}`}
            </DialogTitle>
            <DialogDescription>
              Configure how UniBuy™ punches out INTO this sub-supplier&apos;s webshop and how
              their cXML cart returns are accepted.
            </DialogDescription>
          </DialogHeader>

          {/* Logo upload */}
          <Section title="Logo" subtitle="PNG / JPG / SVG / WEBP up to 2 MB">
            <div className="flex items-center gap-4">
              {form.logo_path ? (
                <img
                  src={`${BACKEND_URL}${form.logo_path}${form.logo_cache_buster ? `?v=${form.logo_cache_buster}` : ""}`}
                  alt="Logo preview"
                  className="h-16 w-16 rounded-md bg-white border border-slate-200 object-contain p-1"
                  data-testid="ss-form-logo-preview"
                />
              ) : form.logo_url ? (
                <img
                  src={form.logo_url}
                  alt="Logo preview"
                  className="h-16 w-16 rounded-md bg-white border border-slate-200 object-contain p-1"
                />
              ) : (
                <div className="h-16 w-16 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 text-xs">
                  No logo
                </div>
              )}
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,.svg,.webp"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (editing && editing !== "new") {
                      await uploadLogo(editing, f);
                    } else {
                      toast.message("Save the sub-supplier first — logo will upload right after.");
                    }
                  }}
                  data-testid="ss-form-logo-file"
                  className="block text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#0072CE]/10 file:text-[#0072CE] hover:file:bg-[#0072CE]/20"
                />
                {logoUploading && (
                  <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                    <Loader2 size={11} className="animate-spin" /> uploading…
                  </div>
                )}
                {form.logo_path && (
                  <div className="text-[10px] font-mono text-slate-400 mt-1 truncate">{form.logo_path}</div>
                )}
              </div>
            </div>
          </Section>

          <Section title="Identity">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Display name *">
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="ss-form-name-input" placeholder="GRAINGER - NORAM - 0030" />
              </FormField>
              <FormField label="Code * (short, uppercase)">
                <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} data-testid="ss-form-code-input" placeholder="GRAINGER" />
              </FormField>
              <FormField label="Contract name" full>
                <Input value={form.contract_name || ""} onChange={(e) => setForm((f) => ({ ...f, contract_name: e.target.value }))} data-testid="ss-form-contract-input" placeholder="Punchout Catalog - 0020220232-GRAINGERWW GRAINGER INC - 0030" />
              </FormField>
              <FormField label="Description" full>
                <Input value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} data-testid="ss-form-description-input" placeholder="Grainger USA" />
              </FormField>
              <FormField label="Tagline (shown on tile)" full>
                <Input value={form.tagline || ""} onChange={(e) => setForm((f) => ({ ...f, tagline: e.target.value }))} data-testid="ss-form-tagline-input" />
              </FormField>
            </div>
          </Section>

          <Section title={form.protocol === "OCI" ? "OCI Dynamic Catalog (SAP) credentials" : "cXML PunchOut credentials"}>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Protocol">
                <select
                  value={form.protocol}
                  onChange={(e) => setForm((f) => ({ ...f, protocol: e.target.value }))}
                  data-testid="ss-form-protocol"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="cXML">cXML (PunchOutSetupRequest)</option>
                  <option value="OCI">OCI (SAP Open Catalog Interface)</option>
                </select>
              </FormField>
              <FormField label={form.protocol === "OCI" ? "OCI Login URL * (SAP StdLogin)" : "PunchOut URL *"} full>
                <Input value={form.punchout_url} onChange={(e) => setForm((f) => ({ ...f, punchout_url: e.target.value }))} data-testid="ss-form-punchout-input" placeholder={form.protocol === "OCI" ? "https://www.lyreco.com/DC/FH1/OCI/Login/StdLogin.do" : "https://b2bconnect.grainger.com/punchout/cxml"} />
              </FormField>
              {form.protocol === "OCI" ? (
                <>
                  <FormField label="USERNAME *">
                    <Input value={form.oci_username || ""} onChange={(e) => setForm((f) => ({ ...f, oci_username: e.target.value }))} data-testid="ss-form-oci-username" placeholder="OCI_JDE" />
                  </FormField>
                  <FormField label="PASSWORD *">
                    <Input value={form.oci_password || ""} onChange={(e) => setForm((f) => ({ ...f, oci_password: e.target.value }))} data-testid="ss-form-oci-password" placeholder="OCI4JDE" />
                  </FormField>
                  <FormField label="LANGUAGE (locale)">
                    <Input value={form.oci_language || ""} onChange={(e) => setForm((f) => ({ ...f, oci_language: e.target.value }))} data-testid="ss-form-oci-language" placeholder="nl-NL" />
                  </FormField>
                  <FormField label="~OKCODE">
                    <Input value={form.oci_okcode || ""} onChange={(e) => setForm((f) => ({ ...f, oci_okcode: e.target.value }))} data-testid="ss-form-oci-okcode" placeholder="ADOI" />
                  </FormField>
                  <FormField label="~TARGET">
                    <Input value={form.oci_target || ""} onChange={(e) => setForm((f) => ({ ...f, oci_target: e.target.value }))} data-testid="ss-form-oci-target" placeholder="_top" />
                  </FormField>
                  <FormField label="~CALLER">
                    <Input value={form.oci_caller || ""} onChange={(e) => setForm((f) => ({ ...f, oci_caller: e.target.value }))} data-testid="ss-form-oci-caller" placeholder="CTLG" />
                  </FormField>
                  <FormField label="HOOK_URL override (optional)" full>
                    <Input value={form.oci_hook_url_override || ""} onChange={(e) => setForm((f) => ({ ...f, oci_hook_url_override: e.target.value }))} data-testid="ss-form-oci-hook" placeholder="Auto: /api/punchout/sub-supplier/return/{CODE}" />
                  </FormField>
                  <FormField label="PO delivery email (UAT)">
                    <Input value={form.oci_po_email_test || ""} onChange={(e) => setForm((f) => ({ ...f, oci_po_email_test: e.target.value }))} data-testid="ss-form-oci-po-test" placeholder="bbn.ecommercenl@lyreco.com" />
                  </FormField>
                  <FormField label="PO delivery email (prod)">
                    <Input value={form.oci_po_email_prod || ""} onChange={(e) => setForm((f) => ({ ...f, oci_po_email_prod: e.target.value }))} data-testid="ss-form-oci-po-prod" placeholder="Customer.nl@lyreco.com" />
                  </FormField>
                </>
              ) : (
                <>
                  <FormField label="Domain (Identity)">
                    <Input value={form.domain} onChange={(e) => setForm((f) => ({ ...f, domain: e.target.value }))} data-testid="ss-form-domain-input" />
                  </FormField>
                  <FormField label="Identity *">
                    <Input value={form.identity || ""} onChange={(e) => setForm((f) => ({ ...f, identity: e.target.value }))} data-testid="ss-form-identity-input" placeholder="159148746" />
                  </FormField>
                  <FormField label="Sender Domain">
                    <Input value={form.sender_domain} onChange={(e) => setForm((f) => ({ ...f, sender_domain: e.target.value }))} data-testid="ss-form-sender-domain-input" />
                  </FormField>
                  <FormField label="Sender Identity *">
                    <Input value={form.sender_identity || ""} onChange={(e) => setForm((f) => ({ ...f, sender_identity: e.target.value }))} data-testid="ss-form-sender-identity-input" placeholder="112574007" />
                  </FormField>
                  <FormField label="Shared Secret *" full>
                    <Input value={form.shared_secret || ""} onChange={(e) => setForm((f) => ({ ...f, shared_secret: e.target.value }))} data-testid="ss-form-secret-input" placeholder="cXML Credential/SharedSecret" />
                  </FormField>
                </>
              )}
            </div>
          </Section>

          <Section title="Commercial">
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Default currency">
                <Input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} data-testid="ss-form-currency-input" maxLength={3} />
              </FormField>
              <FormField label="Default lead-time (days)">
                <Input type="number" value={form.default_lead_days} onChange={(e) => setForm((f) => ({ ...f, default_lead_days: parseInt(e.target.value || "0", 10) }))} data-testid="ss-form-lead-input" min={1} />
              </FormField>
              <FormField label="Default category">
                <Input value={form.default_category || ""} onChange={(e) => setForm((f) => ({ ...f, default_category: e.target.value }))} data-testid="ss-form-category-input" />
              </FormField>
              <FormField label="Default UNSPSC">
                <Input value={form.default_unspsc || ""} onChange={(e) => setForm((f) => ({ ...f, default_unspsc: e.target.value }))} data-testid="ss-form-unspsc-input" placeholder="46181604" />
              </FormField>
              <Toggle label="Advantage account" testid="ss-form-advantage" checked={form.advantage} onChange={(v) => setForm((f) => ({ ...f, advantage: v }))} />
              <Toggle label="Open Buy enabled" testid="ss-form-openbuy" checked={form.open_buy} onChange={(v) => setForm((f) => ({ ...f, open_buy: v }))} />
            </div>
          </Section>

          <Section
            title="Country / region targeting"
            subtitle="Required before enabling"
          >
            <div className="text-[11px] text-slate-500 mb-2 leading-snug">
              Select where this sub-supplier is contractually authorized to serve buyers. Production
              rows must be tagged before they can be enabled. <strong>ALL</strong> means visible to
              every persona; <strong>EU</strong> expands to all 27 EU member states.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {COUNTRY_OPTIONS.map((opt) => {
                const active = (form.countries || []).includes(opt.code);
                const isGroup = opt.code === "ALL" || opt.code === "EU";
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => toggleCountry(opt.code)}
                    data-testid={`ss-form-country-${opt.code}`}
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded border transition ${
                      active
                        ? isGroup
                          ? "bg-violet-600 border-violet-700 text-white"
                          : "bg-[#0072CE] border-[#005BA6] text-white"
                        : isGroup
                          ? "bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100"
                          : "bg-white border-slate-300 text-slate-700 hover:border-[#0072CE] hover:text-[#0072CE]"
                    }`}
                  >
                    {isGroup && <Globe size={11} />}
                    <span className="font-mono">{opt.code}</span>
                    <span className="font-normal text-[10px] opacity-80">· {opt.label.replace(/\(.*\)/,"").trim()}</span>
                  </button>
                );
              })}
            </div>
            {(form.countries || []).length === 0 && form.enabled && (
              <div className="mt-2 text-[11px] text-rose-700 inline-flex items-center gap-1">
                <AlertCircle size={12} /> Country selection required to enable this sub-supplier.
              </div>
            )}
            {!((form.logo_path || "").trim() || (form.logo_url || "").trim() || fileInputRef.current?.files?.[0]) && form.enabled && (
              <div className="mt-2 text-[11px] text-rose-700 inline-flex items-center gap-1">
                <AlertCircle size={12} /> Company logo upload is also required to enable this sub-supplier.
              </div>
            )}
          </Section>

          <Section title="Behaviour">
            <div className="grid grid-cols-2 gap-3">
              <Toggle label="Enabled (shown to buyers)" testid="ss-form-enabled" checked={form.enabled} onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} />
              <Toggle label="Use bundled mock webshop" testid="ss-form-mock" checked={form.mock} onChange={(v) => setForm((f) => ({ ...f, mock: v }))} hint="Disable for production · UniBuy™ will POST a real cXML PunchOutSetupRequest to the URL above" />
              <Toggle label="Require Microsoft AD sign-in" testid="ss-form-ad" checked={form.requires_ad_login} onChange={(v) => setForm((f) => ({ ...f, requires_ad_login: v }))} hint="Prompts buyers for their corporate identity before launching. UPN flows into the cXML so the partner can apply company-specific pricing." full />
            </div>
          </Section>

          {form.protocol === "OCI" && editing !== "new" && (
            <div className="flex items-center gap-2 pt-3 border-t border-slate-100" data-testid="ss-form-oci-conn-test-row">
              <Button type="button" variant="outline" onClick={testConnection} disabled={connTesting} data-testid="ss-form-test-connection">
                {connTesting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <PlugZap size={14} className="mr-1.5" />}
                Test OCI Connection
              </Button>
              {connTest && (
                <span
                  data-testid="ss-form-test-connection-result"
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold ${
                    connTest.ok === true ? "text-emerald-700" : connTest.ok === false ? "text-rose-700" : "text-amber-700"
                  }`}
                >
                  {connTest.ok === true ? <CheckCircle2 size={13} /> : connTest.ok === false ? <XCircle size={13} /> : <HelpCircle size={13} />}
                  {connTest.verdict === "catalog_ok"
                    ? "Catalog reachable"
                    : connTest.verdict === "catalog_error"
                      ? "Vendor rejected login"
                      : connTest.verdict === "unreachable"
                        ? "Endpoint unreachable"
                        : connTest.verdict === "http_error"
                          ? "HTTP error"
                          : "Tested"}
                  {connTest.ai_assisted ? " · AI-verified" : ""}
                </span>
              )}
            </div>
          )}
          {connTest && (
            <div
              className={`mt-2 rounded-md border px-3 py-2 text-[11px] leading-snug ${
                connTest.ok === true
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : connTest.ok === false
                    ? "border-rose-200 bg-rose-50 text-rose-800"
                    : "border-amber-200 bg-amber-50 text-amber-800"
              }`}
              data-testid="ss-form-test-connection-detail"
            >
              <div className="font-semibold">{connTest.reason}</div>
              {connTest.http_status && (
                <div className="mt-0.5 font-mono text-[10px] opacity-80 break-all">
                  HTTP {connTest.http_status} · {connTest.elapsed_ms}ms · {connTest.final_url}
                </div>
              )}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
            <Button onClick={close} variant="ghost" data-testid="ss-form-cancel">
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} data-testid="ss-form-save" className="bg-[#0072CE] hover:bg-[#005BA6] text-white">
              {saving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Save size={14} className="mr-1.5" />}
              {editing === "new" ? "Create Sub-Supplier" : "Save changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="mt-4">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 pb-2 border-b border-slate-100 mb-3 flex items-baseline justify-between">
        <span>{title}</span>
        {subtitle && <span className="text-[10px] text-slate-400 normal-case tracking-normal font-normal">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function FormField({ label, children, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function Toggle({ label, checked, onChange, hint, testid, full }) {
  return (
    <label className={`inline-flex items-start gap-2 text-sm text-slate-700 ${full ? "col-span-2" : "col-span-1"}`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="mt-0.5"
      />
      <div className="leading-snug">
        <div>{label}</div>
        {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
      </div>
    </label>
  );
}
